import { FC, useEffect, useState } from "react";
import { useInvestorClassificationRequirementContext } from "state/investorClassificationRequirement";
import { IMAGES } from "utils/images";
import CorporateInvestorClassificationForm from "./CorporateInvestorClassificationForm";
import IndividualInvestorClassificationForm from "./IndividualInvestorClassificationForm";
import { Card, CloseButton, Overlay } from "./Styled";
import { routesEnum } from "pages/Routes";

// keep in sync with guestOnlyPaths in pages/Routes.tsx
const PUBLIC_PATHS = [
	routesEnum.investorLogin,
	routesEnum.investorLoginV2,
	routesEnum.investorSignUp,
	routesEnum.investorSignUpV2,
	routesEnum.investorSignUpWithSocial,
	routesEnum.yourInvestorSignUp,
	routesEnum.investorSignUpCorporateInvestor,
	routesEnum.signUp,
];

const PopupHeader = () => (
	<>
		<div className="text-xl font-semibold text-center mx-auto mb-2 mt-[-12px]">
			We&apos;ve updated our compliance requirements.
		</div>
		<div className="text-xl font-semibold text-center mx-auto mb-14">
			Please complete the requested information before proceeding.
		</div>
	</>
);

const InvestorClassificationRequirementPopup: FC = () => {
	const {
		accountType,
		isLoggedIn,
		isOpen,
		isLoading,
		close,
		markCompleted,
		complianceInfo,
		investorProfile,
	} = useInvestorClassificationRequirementContext();
	const [isSubmitting, setIsSubmitting] = useState(false);
	const isPublicPath = PUBLIC_PATHS.includes(window.location.pathname as routesEnum);

	useEffect(() => {
		if (!isLoggedIn || !isOpen) return;

		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === "Escape" && !isSubmitting) {
				close();
			}
		};

		document.addEventListener("keydown", handleKeyDown);
		return () => document.removeEventListener("keydown", handleKeyDown);
	}, [close, isLoggedIn, isOpen, isSubmitting]);

	if (!isLoggedIn || !isOpen || isLoading || !accountType || isPublicPath) return null;

	return (
		<Overlay>
			<Card
				role="dialog"
				aria-modal="true"
				aria-labelledby="investor-classification-requirement-title"
				aria-describedby="investor-classification-requirement-subtitle"
			>
				<CloseButton
					type="button"
					aria-label="Close"
					disabled={isSubmitting}
					onClick={close}
				>
					<img src={IMAGES.icon.close} width="14px" height="14px" alt="" />
				</CloseButton>

				<PopupHeader />

				{accountType === "individual" ? (
					<IndividualInvestorClassificationForm
						key={investorProfile?.investorClassificationCriteriaId ?? "individual"}
						onClose={close}
						onCompleted={markCompleted}
						isSubmitting={isSubmitting}
						setIsSubmitting={setIsSubmitting}
					/>
				) : (
					<CorporateInvestorClassificationForm
						key={
							(complianceInfo?.investorClassificationCriteriaIds ?? []).join("-") ||
							"corporate"
						}
						onClose={close}
						onCompleted={markCompleted}
						isSubmitting={isSubmitting}
						setIsSubmitting={setIsSubmitting}
					/>
				)}
			</Card>
		</Overlay>
	);
};

export default InvestorClassificationRequirementPopup;
